import dayjs from 'dayjs';
import React, { Suspense, useEffect, useMemo, useRef } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useWidgetState, useResetState } from './lib/state.ts';
import EventHeader from './EventPage/EventHeader.tsx';
import eventStyles from './EventPage/EventPage.module.css';
import { getEventStep } from './lib/eventFlow.ts';
import { useAtomValue } from 'jotai';
import { AuthAtom } from './lib/state.ts';
import { identify } from '@/lib/analytics/index.ts';
import { IS_EMBED } from '@/lib/isEmbed.ts';
import { useAnnounce } from './lib/announcer.ts';

import { WIDGET_STATE_VERSION } from './lib/types.ts';

let CHECKED = false;

/**
 * A concise, stable heading per flow step for the overlay's polite live region.
 * `confirmed`/`submitted` are matched by substring (they carry a `:uuid`); the
 * rest are exact tails. Empty string → nothing to announce.
 */
const stepAnnouncement = (pathname: string): string => {
  if (pathname.includes('/confirmed')) return 'Booking confirmed';
  if (pathname.includes('/submitted')) return 'Request submitted';
  if (pathname.endsWith('/booking')) return 'Select a date and time';
  if (pathname.endsWith('/details')) return 'Your details';
  if (pathname.endsWith('/verify')) return 'Verify your phone number';
  if (pathname.endsWith('/payment')) return 'Payment';
  if (pathname.endsWith('/checkout')) return 'Review and reserve';
  if (pathname.endsWith('/request') || pathname.endsWith('/steps')) return 'Custom request';
  return '';
};

const SHELL_CSS_VARS = {
  '--button-bg-selected': '#0d0d0d',
  '--button-fg-selected': '#ffffff',
  '--button-bg-unselected': 'rgba(244, 244, 244, 0.6)',
  '--button-fg-unselected': '#0d0d0d',
  '--input-border-focus': '#0d0d0d',
  '--input-border-focus-shadow': 'rgba(13, 13, 13, 0.3)',
  '--content-bg': '#f9f9f9',
  '--max-width': '100%',
  '--container-radius': '0',
} as React.CSSProperties;

// The embed uses the SAME shell vars as the SPA so the flow renders in its
// authentic book.dorsia.com styling; the embed layer only restructures layout
// (see `embed-overrides.css`), never recolors.

const Widget: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [widget] = useWidgetState();
  const auth = useAtomValue(AuthAtom);
  const { pathname } = useLocation();
  const resetState = useResetState();
  const prevIdRef = useRef(id);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const announce = useAnnounce();

  useEffect(() => {
    if (id && prevIdRef.current && id !== prevIdRef.current) {
      resetState();
    }
    prevIdRef.current = id;
  }, [id, resetState]);

  // Identify the member at the shell, as early as auth is known, so the
  // top-of-funnel events fired from the booking/details screens (`Widget
  // Loaded`, `Widget Schedule Viewed`, …) carry the user id too. `useUserDetails`
  // only mounts on later screens (payment/checkout/confirmed), so relying on it
  // alone would leave a returning member's early funnel anonymous. `auth.user`
  // is the member uuid, read synchronously from the persisted atom — no API
  // call — and `useUserDetails` later enriches the same uuid with profile
  // traits (idempotent).
  useEffect(() => {
    if (auth?.user) {
      identify(auth.user);
    }
  }, [auth?.user]);

  const { step, totalSteps } = useMemo(
    () => getEventStep(pathname, !!auth?.token, widget.bookingType),
    [pathname, auth?.token, widget.bookingType]
  );

  useEffect(() => {
    // Scroll the flow's own wrapper into view on step change. Using a ref
    // (instead of `document.getElementById('root')`) keeps this correct inside
    // the embed's shadow root, where there is no document-level `#root`.
    wrapperRef.current?.scrollIntoView();
    // Announce the new step to assistive tech (no-op in the SPA — no announcer).
    announce(stepAnnouncement(pathname));
  }, [pathname, announce]);

  useEffect(() => {
    // In embed mode the glass stage is the background; tinting the host page's
    // body would bleed into the restaurant's own site.
    if (IS_EMBED) return;
    const prev = document.body.style.backgroundColor;
    document.body.style.backgroundColor = '#f9f9f9';
    return () => {
      document.body.style.backgroundColor = prev;
    };
  }, []);

  useEffect(() => {
    if (widget.date && !CHECKED) {
      CHECKED = true;
      const version: string | null = widget.version ?? null;
      if (version !== WIDGET_STATE_VERSION) {
        console.info('Starting Over due to Newer Data Version');
        resetState();
        navigate(`/restaurant/${id}/booking`, { replace: true });
      } else if (dayjs().diff(widget.expires) > 0) {
        console.info('Starting Over due to Expiration');
        resetState();
        navigate(`/restaurant/${id}/booking`, { replace: true });
      }
    }
  }, [widget.date]);

  return (
    <div
      ref={wrapperRef}
      data-dorsia-flow-root={IS_EMBED ? '' : undefined}
      className={eventStyles.eventPageWrapper}
      style={SHELL_CSS_VARS}
    >
      <EventHeader step={step} totalSteps={totalSteps} />

      <Suspense
        fallback={
          <div className={eventStyles.eventContent}>
            <div className={eventStyles.card} style={{ padding: 40, textAlign: 'center' }}>
              <p
                style={{
                  fontFamily: "'Sailec', sans-serif",
                  fontSize: 20,
                  fontWeight: 500,
                  color: '#0d0d0d',
                }}
              >
                Loading...
              </p>
            </div>
          </div>
        }
      >
        <Outlet />
      </Suspense>
    </div>
  );
};

export default Widget;
